import {
  BebopperIcon,
  OgAchievoorIcon,
  OgCommunityCallFrenIcon,
  OgContributoorIcon,
  OgOverachievoorIcon,
  OgTestoorIcon,
  OgTradoorIcon,
  PinHoldoorIcon,
  QuestoorIcon,
  TwitterMaxiIcon,
} from "components/Icons";
import React from "react";
import { Address } from "viem";
import { arbitrum, Chain, mainnet, polygon } from "viem/chains";
import {
  ARBITRUM_TOKEN_ADDRESSES_TO_SYMBOL_MAP,
  ARBITRUM_TOKENS_ADDRESSES,
  ETH_TOKEN_ADDRESSES,
  ETH_TOKEN_ADDRESSES_TO_SYMBOL_MAP,
  POLYGON_TOKEN_ADDRESSES_TO_SYMBOL_MAP,
  POLYGON_TOKENS_ADDRESSES,
  Token,
} from "../store/global/TokenEnum";
import { Amount } from "../types/amount";

export const PROJECT_NAME = "Bebop";

export enum BEBOP_ROUTES {
  Home = "/",
  Trade = "/trade",
  Activity = "/activity",
  PageNotFound = "*",
}
export const MAX_SELECTABLE_TOKENS = 5;
export const NATIVE_TOKENS = ["ETH", "BTC", "MATIC"];
export const QUOTE_REFETCH_INTERVAL_MS = 10_000;
export const BALANCE_REFETCH_INTERVAL_MS = 30_000;
export const TOKEN_REFETCH_INTERVAL_MS = 30_000;
export const FETCH_BALANCE_RETRY_TIMES = 3;
export const FETCH_TOKENS_RETRY_TRIES = 3;
export const USER_INPUT_DEBOUNCE_TIME_MS = 250;
export const DIGITS_TO_SHOW_ON_BALANCE = 5;
export const DIGITS_TO_SHOW_ON_CONFIRM = 5;
export const QUOTE_DEBOUNCE_TIME = 1000;

export const SEPARATOR_DOT = "•";
export const BEBOP_GUILD_ID = 11177;
export const GUILD_NAMES_TO_ID = {
  OG_CONTRIBUTOOR: 23153,
  CONTRIBUTOOR: 43738,
  OG_OVERACHIEVOOR: 21322,
  OG_TRADOOR: 21320,
  OG_ACHIEVOOR: 21321,
  OG_TESTOOR: 21319,
  OG_COMMUNITY_CALL_FREN: 17064,
  PIN_HOLDOOR: 56300,
  QUESTOOR: 28281,
  TWITTER_MAXI: 17063,
  BEBOPPER: 56299,
  TRADOOR: 61741,
  DEV_MOCK: 99999,
};
export const GUILD_ROLES_PRECEDENCE: Record<number, number> = [
  GUILD_NAMES_TO_ID.OG_CONTRIBUTOOR,
  GUILD_NAMES_TO_ID.OG_ACHIEVOOR,
  GUILD_NAMES_TO_ID.OG_TRADOOR,
  GUILD_NAMES_TO_ID.OG_ACHIEVOOR,
  GUILD_NAMES_TO_ID.OG_TESTOOR,
  GUILD_NAMES_TO_ID.TRADOOR,
  GUILD_NAMES_TO_ID.PIN_HOLDOOR,
  GUILD_NAMES_TO_ID.QUESTOOR,
  GUILD_NAMES_TO_ID.OG_COMMUNITY_CALL_FREN,
  GUILD_NAMES_TO_ID.TWITTER_MAXI,
  GUILD_NAMES_TO_ID.BEBOPPER,
].reduce((prev, curr, idx) => {
  return {
    ...prev,
    [curr]: idx,
  };
}, {});

export const GUILD_ROLES_EMOJIS: Record<number, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [GUILD_NAMES_TO_ID.OG_CONTRIBUTOOR]: OgContributoorIcon,
  [GUILD_NAMES_TO_ID.OG_OVERACHIEVOOR]: OgOverachievoorIcon,
  [GUILD_NAMES_TO_ID.OG_TRADOOR]: OgTradoorIcon,
  [GUILD_NAMES_TO_ID.OG_ACHIEVOOR]: OgAchievoorIcon,
  [GUILD_NAMES_TO_ID.OG_TESTOOR]: OgTestoorIcon,
  [GUILD_NAMES_TO_ID.QUESTOOR]: QuestoorIcon,
  [GUILD_NAMES_TO_ID.PIN_HOLDOOR]: PinHoldoorIcon,
  [GUILD_NAMES_TO_ID.TWITTER_MAXI]: TwitterMaxiIcon,
  [GUILD_NAMES_TO_ID.BEBOPPER]: BebopperIcon,
  [GUILD_NAMES_TO_ID.TRADOOR]: OgTradoorIcon,
  [GUILD_NAMES_TO_ID.OG_COMMUNITY_CALL_FREN]: OgCommunityCallFrenIcon,
  [GUILD_NAMES_TO_ID.DEV_MOCK]: BebopperIcon,
};

export const TOKEN_DECIMALS_MAP: Partial<Record<Token, number>> = {
  [Token["1INCH"]]: 18,
  [Token.AAVE]: 18,
  [Token.AMP]: 18,
  [Token.APE]: 18,
  [Token.BAL]: 18,
  [Token.BAND]: 18,
  [Token.BAT]: 18,
  [Token.BLUR]: 18,
  [Token.BUSD]: 18,
  [Token.CBETH]: 18,
  [Token.CEL]: 4,
  [Token.COMP]: 18,
  [Token.CRO]: 8,
  [Token.CRV]: 18,
  [Token.CTX]: 18,
  [Token.DAI]: 18,
  [Token.DPI]: 18,
  [Token.DYDX]: 18,
  [Token.ENS]: 18,
  [Token.ETH]: 18,
  [Token.FET]: 18,
  [Token.FTM]: 18,
  [Token.FTT]: 18,
  [Token.GALA]: 8,
  [Token.GALA_OLD]: 8,
  [Token.GALA_V1]: 8,
  [Token.GMX]: 18,
  [Token.GRT]: 18,
  [Token.HFT]: 18,
  [Token.IMX]: 18,
  [Token.LDO]: 18,
  [Token.LINK]: 18,
  [Token.MAGIC]: 18,
  [Token.MPL]: 18,
  [Token.MATIC]: 18,
  [Token.MKR]: 18,
  [Token.OMG]: 18,
  [Token.PAX]: 18,
  [Token.PERP]: 18,
  [Token.PEPE]: 18,
  [Token.RDNT]: 18,
  [Token.REN]: 18,
  [Token.RENBTC]: 8,
  [Token.SAND]: 18,
  [Token.SHIB]: 18,
  [Token.SUSHI]: 18,
  [Token.TUSD]: 18,
  [Token.UNI]: 18,
  [Token.USDC]: 6,
  [Token.USDP]: 18,
  [Token.USDT]: 6,
  [Token.USTC]: 18,
  [Token.WBTC]: 8,
  [Token.WETH]: 18,
  [Token.WMATIC]: 18,
  [Token.YFI]: 18,
  [Token.YGG]: 18,
  [Token.ZRX]: 18,
};

export const DUMMY_WALLET = "0x0000000000000000000000000000000000000001";
export const BLOCK_EXPLORER_URL_BY_NETWORK: Record<number, string> = {
  [mainnet.id]: "https://etherscan.io/",
  [polygon.id]: "https://polygonscan.com/",
  [arbitrum.id]: "https://arbiscan.io/",
};
export const NETWORK_NAME_TO_DISPLAY: Record<number, string> = {
  [mainnet.id]: mainnet.name,
  [polygon.id]: polygon.name,
  [arbitrum.id]: "Arbitrum",
};
export const NETWORK_ID_TO_NETWORK_NAME_MAP: Record<number, string> = {
  [mainnet.id]: "ethereum",
  [polygon.id]: "polygon",
  [arbitrum.id]: "arbitrum",
};

export const GAS_ESTIMATE_MULTIPLIER = BigInt(2);

export const TWITTER_IMAGE_URL = "https://twitter.com/";
export const TWITTER_IMAGE_URLS = [
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206635948675075/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206432894222336/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206386643443712/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206339839365120/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206251897421824/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206201741930496/photo/1`,
];

export const TIME_BEFORE_USER_IS_IDLE = 60_000;

export const FALLBACK_DISPLAY_DECIMALS = 5;

export const NAV_LINKS = {
  BEBOP: "https://bebop.xyz",
  DISCORD: "https://discord.com/invite/bebop",
  TWITTER: "https://twitter.com/bebop_dex",
  MEDIUM: "https://medium.com/bebop-dex",
  BLOG: "https://medium.com/bebop-dex",
  FAQ: "https://help.bebop.xyz/en",
  FAQ_DEVELOPERS: "https://help.bebop.xyz/en/collections/3939904-bebop-api",
  JOBS: "https://jobs.lever.co/Bebop",
  EMAIL: "mailto:hello@bebop.xyz",
  EMAIL_DEVELOPERS: "mailto:api@bebop.xyz",
  BRAND: "https://docs.bebop.xyz/bebop/brand-guidance/logos",
  TERMS_AND_CONDITIONS: "/terms.pdf",
  PRIVACY_POLICY: "/privacy.pdf",
  DOCUMENTATION: "https://docs.bebop.xyz",
  DOCUMENTATION_BEBOP_INTRODUCTION: "https://docs.bebop.xyz/bebop",
  DOCUMENTATION_API_INTRODUCTION: "https://docs.bebop.xyz/bebop/bebop-api/api-introduction",
  BADGES: "https://bebop.rep3.gg/",
  GUILD: "https://guild.xyz/bebop",

  WINTERMUTE: "https://www.wintermute.com",

  FORM_MARKET_MAKERS: "https://survey.typeform.com/to/oNtFKFm3",
  FORM_API_INTEGRATION: "https://survey.typeform.com/to/QdsBd1bp",
  FORM_SOLVERS: "https://survey.typeform.com/to/zZVtZdId",
  FORM_REACH_OUT: "https://survey.typeform.com/to/oy4NaTbn",

  AUDIT_REPORT_MIXBYTES: "https://github.com/mixbytes/audits_public/tree/master/Bebop",
  AUDIT_REPORT_PESSIMISTIC:
    "https://github.com/pessimistic-io/audits/blob/eb05b29457fc966865e91e9e905f58b4e8e7679b/Bebop%20Security%20Analysis%20by%20Pessimistic.pdf",
  AUDIT_REPORT_DECURITY:
    "https://github.com/Decurity/audits/blob/master/Bebop/bebop-jam-audit-report-1.1.pdf",

  DOCUMENTATION_MANAGE_PERMISSIONS:
    "https://help.bebop.xyz/en/articles/6499751-why-do-i-need-to-manage-permissions-and-approve-the-use-of-tokens",
  TOKENS_THAT_CAN_BE_TRADED:
    "https://help.bebop.xyz/en/articles/6499728-what-tokens-can-i-trade-on-bebop",
};

export const SOCIAL_CTA_LINKS = {
  SHARE_TWEET_PREFIX: "https://twitter.com/intent/tweet?text=",
};

export const ACCORDIONS = {
  SWAPANDSEND: "swap-and-send",
  DETAILS: "trade-details",
} as const;

export const PERMIT_TWO_CONTRACT_ADDRESS = "0x000000000022d473030f116ddee9f6b43ac78ba3";
export const MAX_UINT_256 = BigInt(
  "115792089237316195423570985008687907853269984665640564039457584007913129639935"
);
export const MAX_UINT_160 = BigInt("1461501637330902918203684832716283019655932542975");

export const USDT_CONTRACTS = {
  [mainnet.id]: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
};

export const OMG_CONTRACTS = {
  [mainnet.id]: "0xd26114cd6EE289AccF82350c8d8487fedB8A0C07",
};

export const ALCHEMY_API_KEY = "GjpKGlKJy4WtofaSCs_5_B7NZSX8K0vw";
export const ZERION_NFT_CONTRACT = "0x932261f9fc8da46c4a22e31b45c4de60623848bf";

export const DEFAULT_DECIMAL_SEPARATOR = ".";

export const SENTRY_CONTEXTS = {
  APPROVALS: "approvals",
  BALANCES: "balances",
  ALLOWANCES: "allowances",
};

export const enum Filter {
  Network = "network",
  Transaction = "transaction",
  Token = "token",
  Amount = "amount",
  Date = "date",
}
export const enum TransactionType {
  Trade = "Trade",
  TradeAndSend = "Trade And Send",
}

export interface TransactionTypeProps {
  icon: React.ReactNode;
}

export interface IAmountFilter {
  min: Amount | undefined;
  max: Amount | undefined;
}

export interface FiltersInterface {
  [Filter.Network]: Array<Chain["name"]> | undefined;
  [Filter.Transaction]: Array<TransactionType> | undefined;
  [Filter.Token]: Array<Token> | undefined;
  [Filter.Amount]: IAmountFilter | undefined;
  [Filter.Date]: Array<string> | undefined;
}

export const FilterOptions = {
  Network: [mainnet.name, polygon.name, arbitrum.name],
  Transaction: ["Trade", "Approval", "Revoke", "Wrapping", "Unwrapping"],
  Token: Object.keys(Token) as Array<Token>,
  Date: ["All", "1 Day", "1 Week", "1 Month", "3 Months", "6 Months", "12 Months"],
};

export const NUM_TRADES_TO_FETCH = 500;
export const DAI_LIKE_PERMIT1_TOKENS = [Token.RENBTC, Token.DAI];

/**
 * Some tokens require a revoke before the increasing allowances/approvals
 */
export const REVOKE_BEFORE_APPROVAL_REQUIRED = [Token.CRV, Token.LDO];

export const BEBOP_CONTRACT = "0xbEbEbEb035351f58602E0C1C8B59ECBfF5d5f47b";
export const PMM_CONTRACT = "0xBeB09000fa59627dc02Bb55448AC1893EAa501A5";

export const NETWORK_TO_TOKEN_ADDRESS_MAP: Record<number, Record<string, `0x${string}`>> = {
  [mainnet.id]: ETH_TOKEN_ADDRESSES,
  [polygon.id]: POLYGON_TOKENS_ADDRESSES,
  [arbitrum.id]: ARBITRUM_TOKENS_ADDRESSES,
};
export const NETWORK_TO_TOKEN_SYMBOL_MAP: Record<number, Record<string, string>> = {
  [mainnet.id]: ETH_TOKEN_ADDRESSES_TO_SYMBOL_MAP,
  [polygon.id]: POLYGON_TOKEN_ADDRESSES_TO_SYMBOL_MAP,
  [arbitrum.id]: ARBITRUM_TOKEN_ADDRESSES_TO_SYMBOL_MAP,
};

export const NETWORK_TO_BEBOP_CONTRACT_ADDRESS_MAP: Record<number, `0x${string}`> = {
  [mainnet.id]: BEBOP_CONTRACT,
  [polygon.id]: BEBOP_CONTRACT,
  [arbitrum.id]: BEBOP_CONTRACT,
};

export const VALID_APPROVAL_SPENDER = "0xfE96910cF84318d1B8a5e2a6962774711467C0be";

export const VALID_APPROVAL_SPENDERS = [PMM_CONTRACT, VALID_APPROVAL_SPENDER];

export const WRAPPED_ABI = [
  {
    constant: true,
    inputs: [],
    name: "name",
    outputs: [{ name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "guy", type: "address" },
      { name: "wad", type: "uint256" },
    ],
    name: "approve",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "totalSupply",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "src", type: "address" },
      { name: "dst", type: "address" },
      {
        name: "wad",
        type: "uint256",
      },
    ],
    name: "transferFrom",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: false,
    inputs: [{ name: "wad", type: "uint256" }],
    name: "withdraw",
    outputs: [],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "decimals",
    outputs: [{ name: "", type: "uint8" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [{ name: "", type: "address" }],
    name: "balanceOf",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: true,
    inputs: [],
    name: "symbol",
    outputs: [{ name: "", type: "string" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  {
    constant: false,
    inputs: [
      { name: "dst", type: "address" },
      { name: "wad", type: "uint256" },
    ],
    name: "transfer",
    outputs: [{ name: "", type: "bool" }],
    payable: false,
    stateMutability: "nonpayable",
    type: "function",
  },
  {
    constant: false,
    inputs: [],
    name: "deposit",
    outputs: [],
    payable: true,
    stateMutability: "payable",
    type: "function",
  },
  {
    constant: true,
    inputs: [
      { name: "", type: "address" },
      { name: "", type: "address" },
    ],
    name: "allowance",
    outputs: [{ name: "", type: "uint256" }],
    payable: false,
    stateMutability: "view",
    type: "function",
  },
  { payable: true, stateMutability: "payable", type: "fallback" },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "src", type: "address" },
      {
        indexed: true,
        name: "guy",
        type: "address",
      },
      { indexed: false, name: "wad", type: "uint256" },
    ],
    name: "Approval",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "src", type: "address" },
      {
        indexed: true,
        name: "dst",
        type: "address",
      },
      { indexed: false, name: "wad", type: "uint256" },
    ],
    name: "Transfer",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "dst", type: "address" },
      {
        indexed: false,
        name: "wad",
        type: "uint256",
      },
    ],
    name: "Deposit",
    type: "event",
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, name: "src", type: "address" },
      {
        indexed: false,
        name: "wad",
        type: "uint256",
      },
    ],
    name: "Withdrawal",
    type: "event",
  },
] as const;

export const CONTRACT_TO_PERMIT_DOMAIN_MAPPING: {
  [chainId: Chain["id"]]: { [address: Address]: number };
} = {
  1: {
    "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE": 0,
    "0x31c8EAcBFFdD875c74b94b077895Bd78CF1E64A3": 0,
    "0x6De037ef9aD2725EB40118Bb1702EBb27e4Aeb24": 0,
    "0x1cEB5cB57C4D4E2b2433641b95Dd330A33185A44": 0,
    "0x6B175474E89094C44Da98b954EedeAC495271d0F": 1,
    "0x7Fc66500c84A76Ad7e9c93437bFc5Ac33E2DDaE9": 1,
    "0xba100000625a3754423978a60c9317c58a424e3D": 1,
    "0x92D6C1e31e14520e676a687F0a93788B716BEff5": 1,
    "0xb3999F658C0391d94A37f7FF328F3feC942BcADC": 2,
    "0xc944E90C64B2c07662A292be6244BDf05Cda44a7": 4,
    "0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984": 2,
    "0x111111111117dC0aa78b770fA6A738034120C302": 1,
    "0xd1d2Eb1B1e90B638588728b4130137D262C87cae": 1,
    "0x8E870D67F660D95d5be530380D0eC0bd388289E1": 0,
    "0x7D1AfA7B718fb893dB30A3aBc0Cfc608AaCfeBB0": 0,
    "0x6982508145454Ce325dDbE47a25d4ec3d2311933": 0,
    "0xE41d2489571d322189246DaFA5ebDe1F4699F498": 0,
    "0xD533a949740bb3306d119CC777fa900bA034cd52": 0,
    "0x4E15361FD6b4BB609Fa63C81A2be19d873717870": 0,
    "0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE": 0,
    "0x3845badAde8e6dFF049820680d1F14bD3903a5d0": 0,
    "0x0F5D2fB29fb7d3CFeE444a200298f468908cC942": 0,
    "0xc221b7E65FfC80DE234bbB6667aBDd46593D34F0": 0,
    "0x4Fabb145d64652a948d72533023f6E7A623C7C53": 0,
    "0xdAC17F958D2ee523a2206206994597C13D831ec7": 0,
    "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2": 0,
    "0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599": 0,
    "0x0D8775F648430679A709E98d2b0Cb6250d2887EF": 0,
    "0x514910771AF9Ca656af840dff83E8264EcF986CA": 0,
    "0xc00e94Cb662C3520282E6f5717214004A7f26888": 0,
    "0xaea46A60368A7bD060eec7DF8CBa43b7EF41Ad85": 0,
    "0x5A98FcBEA516Cf06857215779Fd812CA3beF1B32": 0,
    "0x9f8F72aA9304c8B593d555F12eF6589cC3A579A2": 0,
    "0x6B3595068778DD592e39A122f4f5a5cF09C90fE2": 0,
    "0x0bc529c00C6401aEF6D220BE8C6Ea1667F6Ad93e": 0,
    "0x25f8087EAD173b73D6e8B84329989A8eEA16CF73": 0,
    "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48": 1,
    "0x5283D291DBCF85356A21bA090E6db59121208b44": 1,
    "0xBe9895146f7AF43049ca1c1AE358B0541Ea49704": 1,
    "0x321C2fE4446C7c963dc41Dd58879AF648838f98D": 2,
    "0x967da4048cD07aB37855c090aAF366e4ce1b9F48": 0,
    "0x163f8C2467924be0ae7B5347228CABF260318753": 0,
  },
  137: {
    "0xBbba073C31bF03b8ACf7c28EF0738DeCF3695683": 0,
    "0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6": 0,
    "0xD6DF932A45C0f255f85145f286eA0b292B21C90B": 0,
    "0x9a71012B13CA4d3D0Cdc72A177DF3ef03b0E76A3": 0,
    "0x53E0bca35eC356BD5ddDFebbD1Fc0fD03FaBad39": 0,
    "0x172370d5Cd63279eFa6d502DAB29171933a610AF": 0,
    "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270": 0,
    "0xc2132D05D31c914a87C6611C10748AEb04B58e8F": 0,
    "0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619": 0,
    "0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174": 3,
    "0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063": 3,
    "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE": 0,
  },
  42161: {
    "0x82aF49447D8a07e3bd95BD0d56f35241523fBab1": 1,
    "0x2f2a2543B76A4166549F7aaB2e75Bef0aefC5B0f": 1,
    "0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1": 0,
    "0xfc5A1A6EB076a2C7aD06eD22C90d7E710E35ad0a": 0,
    "0x539bdE0d7Dbd336b79148AA742883198BBF60342": 1,
    "0x3082CC23568eA640225c2467653dB90e9250AaA0": 0,
    "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9": 1,
    "0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8": 1,
    "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE": 0,
  },
};

// For framer-motion
export const SPRING_ANIMATION = { type: "spring", damping: 30, mass: 1, stiffness: 400 };

// REMOVE when we move to indexing by contract address
export const BRIDGED_USDC_ARB_ADDRESS = "0xFF970A61A04b1cA14834A43f5dE4533eBDDB5CC8";

export const BALANCE_CONTRACTS: Record<Chain["id"], `0x${string}`> = {
  [mainnet.id]: "0x21486E4427031F10fBE45F47AAd6f729014d42a4",
  [polygon.id]: "0x132F13C3896eAB218762B9e46F55C9c478905849",
  [arbitrum.id]: "0xf8D3B856cc0A9c99A5b849246b8E679E32298E1c",
};

export const Z_INDICES = {
  BACKGROUND: 0,
  CONTENT_BASE: 1000,
  CONTENT_SURFACE: 5000,
  NAV_BAR: 10_000,
  POP_UPS: 100_000,
  SOCIALS: 100_000,
};

export const AMOUNT_TO_SHOW_TRADE_SURPLUS = 1;
